import { toDateId, startOfMonth } from '@calendly/datetime'
import { debounce } from 'lodash-es'
import { MAX_CACHE_AGE } from '@calendly/booking-kit/constants'
import { parseQuery } from '@calendly/http-tools'

import { storeTimeNotation } from 'services/time_notation'
import { fetchRangeData, buildRanges } from 'services/calendar'
import {
  canProcessRangesInParallel,
  isPollSelector
} from 'selectors/event_type'
import {
  calendarSelector,
  receivedAtSelector,
  timezoneSelector
} from 'selectors'
import { isLocalStorageAllowedSelector } from 'selectors/gdpr'
import { setCurrentUser } from 'actions/current_user'
import { isDiagnosticsEnabledSelector } from 'selectors/diagnostics'
import { currentUserEmailSelector } from 'selectors/user'
import { isPopupTextEmbedTypeSelector } from 'selectors/url'
import { syncUrlParameters, syncSpotUrlParameters } from 'actions/navigation'
import { deselectCalendarDay } from 'actions/calendar_day'
import { addSpotFromParams } from 'actions/spot'
import { eventTypeSelector } from 'root-api'

import { setGeneralError } from './errors'


const REQUEST_DEBOUNCE = 800

const setCalendarLoading = (monthId) => ({
  type: 'FETCH_CALENDAR_REQUEST',
  payload: {
    monthId
  }
})

const setFetchRangeDone = (monthId, days, range) => ({
  type: 'FETCH_CALENDAR_RANGE_DONE',
  payload: {
    monthId,
    range_end: range[1],
    days
  }
})

const setFetchCalendarDone = (monthId, diagnosticData) => ({
  type: 'FETCH_CALENDAR_DONE',
  payload: {
    monthId,
    diagnosticData
  }
})

const setCalendarError = (monthId) => ({
  type: 'FETCH_CALENDAR_FAIL',
  payload: {
    monthId
  }
})

const setInviteePublisherError = () => ({
  type: 'SET_INVITEE_PUBLISHER_ERROR'
})

const shouldFetchCalendar = (state, monthId) => {
  const calendar = calendarSelector(state, monthId)
  const receivedAt = receivedAtSelector(state)
  if (!calendar) return true

  switch (calendar.status) {
    case 'loading':
      return false
    case 'ready':
      return Math.floor((Date.now() - receivedAt) / 1000) > MAX_CACHE_AGE
    default:
      return true
  }
}

export const clearCalendar = () => ({
  type: 'CLEAR_CALENDAR_DATA'
})

const findFailedMonthIds = (state) => (
  Object.entries(state.calendars).reduce((memo, [monthId, calendar]) => {
    if (monthId === 'options') return memo
    if (calendar.status === 'error') memo.push(monthId)
    return memo
  }, [])
)

export const changeCalendarMonth = (count) => ({
  type: 'CHANGE_CALENDAR_MONTH',
  count
})

const selectSpotsFromDays = (dispatch, days) => {
  days.forEach(({ spots }) => spots.forEach((spot) => {
    if (spot.selected) {
      dispatch(addSpotFromParams(spot))
    }
  }))
}

const selectSpotsFromParams = (dispatch, spotsFromParams, days) => {
  if (!spotsFromParams) {
    selectSpotsFromDays(dispatch, days)
    return
  }

  const parsedSpots = spotsFromParams.split(',')

  days.forEach(({ spots }) => spots.forEach((spot) => {
    if (parsedSpots.includes(spot.start_time)) {
      dispatch(addSpotFromParams(spot))
    }
  }))
}

const processDiagnosticData = (responseData, diagnosticData) => {
  if (responseData.diagnostic_data) {
    responseData.diagnostic_data.holidays = { ...diagnosticData.holidays, ...responseData.diagnostic_data.holidays }
    Object.assign(diagnosticData, responseData.diagnostic_data)
  }
}

const processInviteePublisherError = (dispatch, responseData) => {
  if (responseData.invitee_publisher_error) {
    dispatch(setInviteePublisherError())
  }
}

const processUserChanging = (dispatch, state, responseData) => {
  if (currentUserEmailSelector(state) !== responseData.current_user.email && !isPopupTextEmbedTypeSelector(state)) {
    dispatch(clearCalendar())
    dispatch(deselectCalendarDay())
    dispatch(setCurrentUser(responseData.current_user))
  }
}

const processPollSpots = (dispatch, state, responseData) => {
  if (isPollSelector(state)) {
    const query = parseQuery(window.location.search)
    selectSpotsFromParams(dispatch, query.spots, responseData.days)
    dispatch(syncSpotUrlParameters())
  }
}

const processRangeFailure = (dispatch, monthId, failure) => {
  dispatch(setCalendarError(monthId))
  dispatch(setGeneralError(failure))
}

const processRangeSuccess = ({ dispatch, state, monthId, responseData, range, diagnosticData }) => {
  processUserChanging(dispatch, state, responseData)
  processPollSpots(dispatch, state, responseData)
  processInviteePublisherError(dispatch, responseData)
  processDiagnosticData(responseData, diagnosticData)
  dispatch(setFetchRangeDone(monthId, responseData.days, range))
}

const processRangesInParallel = async (dispatch, state, monthId, ranges, duration) => {
  const rangesData = await Promise.all(
    ranges.map((range) => fetchRangeData(state, range, duration))
  )

  if (rangesData.every(([result]) => result === true)) {
    const diagnosticData = {}
    rangesData.forEach(([, responseData], index) => {
      const range = ranges[index]
      processRangeSuccess({ dispatch, state, monthId, responseData, range, diagnosticData })
    })
    dispatch(setFetchCalendarDone(monthId, diagnosticData))
  } else {
    const [, failure] = rangesData.find(([result]) => result === false)
    processRangeFailure(dispatch, monthId, failure)
  }
}

const processRangesSequentially = async (dispatch, state, monthId, ranges, duration) => {
  const diagnosticData = {}
  for await (const range of ranges) {
    const [success, responseData] = await fetchRangeData(state, range, duration)
    if (success) {
      processRangeSuccess({ dispatch, state, monthId, responseData, range, diagnosticData })
    } else {
      processRangeFailure(dispatch, monthId, responseData)
      return
    }
  }
  dispatch(setFetchCalendarDone(monthId, diagnosticData))
}

const doFetchCalendar = (dispatch, state, monthId, duration) => {
  dispatch(setCalendarLoading(monthId))
  const ranges = buildRanges(
    monthId,
    eventTypeSelector(state).multi_publisher,
    isDiagnosticsEnabledSelector(state),
    timezoneSelector(state),
  )

  if (canProcessRangesInParallel(state)) {
    processRangesInParallel(dispatch, state, monthId, ranges, duration)
  } else {
    processRangesSequentially(dispatch, state, monthId, ranges, duration)
  }
}

const doDebouncedFetchCalendar = debounce(doFetchCalendar, REQUEST_DEBOUNCE)

export const fetchFailedCalendars = () => (dispatch, getState) => {
  const state = getState()
  const monthIds = findFailedMonthIds(state)
  return monthIds.forEach((monthId) => doFetchCalendar(dispatch, state, monthId))
}

export const fetchCalendar = (date, duration) => (dispatch, getState) => {
  const state = getState()
  const monthId = toDateId(startOfMonth(date))

  if (shouldFetchCalendar(state, monthId)) {
    if (Object.keys(state.calendars).length <= 1) {
      doFetchCalendar(dispatch, state, monthId, duration)
    } else {
      doDebouncedFetchCalendar(dispatch, state, monthId, duration)
    }
  }

  dispatch(syncUrlParameters(duration))
}

export const changeTimeNotation = (notation) => (dispatch, getState) => {
  if (isLocalStorageAllowedSelector('functional')(getState())) {
    storeTimeNotation(notation)
  }

  dispatch({
    type: 'CHANGE_TIME_NOTATION',
    notation
  })
}

export const updateSelectedSpotsByTimezone = (timezone) => ({
  type: 'UPDATE_SELECTED_SPOTS_BY_TIMEZONE',
  timezone
})
